// TEXT

// STYLES

// DATA

// TYPES
export type Props = {
  fieldName: string;
  fieldValue: unknown;
  onFieldChange: (value: string | null) => void;
  onFieldBlur: () => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  hasError: boolean;
};

type DatePart = "year" | "month" | "day";

export type LocaleDateConfig = {
  order: DatePart[];
  separator: string;
  placeholder: string;
  pattern: string;
};

// CONSTANTS
export const FALLBACK_LOCALE = "en-US";
// Reference date with distinct day/month digits so we can detect part order.
const REFERENCE_DATE = new Date(Date.UTC(2000, 0, 2));

// FUNCTIONS
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Derives how a given locale writes a numeric date: the part order
 * (e.g. MM/DD/YYYY vs. DD.MM.YYYY), the separator, a display placeholder, and a
 * matching validation pattern. Lets the date input adapt its placeholder,
 * `pattern`, and parsing to the user's locale instead of hardcoding a format.
 */
export const getLocaleDateConfig = (locale: string): LocaleDateConfig => {
  const parts = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "UTC",
  }).formatToParts(REFERENCE_DATE);

  const order: DatePart[] = [];
  let separator = "/";
  let separatorFound = false;
  for (const part of parts) {
    if (part.type === "year" || part.type === "month" || part.type === "day") {
      order.push(part.type);
    } else if (part.type === "literal" && !separatorFound) {
      separator = part.value;
      separatorFound = true;
    }
  }

  const placeholder = order
    .map((p) => (p === "year" ? "YYYY" : p === "month" ? "MM" : "DD"))
    .join(separator);

  const pattern = order
    .map((p) => (p === "year" ? "\\d{4}" : "\\d{2}"))
    .join(escapeRegex(separator));

  return { order, separator, placeholder, pattern };
};

/**
 * Formats a stored field value (an epoch-millisecond timestamp, as a string or
 * number) into a locale-formatted date string for display in the input.
 * Returns "" for empty or unparseable values so the field renders blank rather
 * than "Invalid Date".
 */
export const timestampToLocaleString = (
  value: unknown,
  locale: string,
): string => {
  if (value === null || value === undefined || value === "") return "";

  const ms = Number(value);
  if (!Number.isFinite(ms)) return "";

  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

/**
 * Parses what the user typed (a locale-formatted date string) back into an
 * epoch-millisecond timestamp string for form state. Returns null when the
 * input is incomplete or not a real calendar date, so mid-typing or invalid
 * dates don't commit a bad value.
 */
export const localeStringToTimestamp = (
  input: string,
  locale: string,
): string | null => {
  if (!input) return null;

  const { order, separator } = getLocaleDateConfig(locale);
  const segments = input.split(separator);
  if (segments.length !== 3) return null;

  const parts: Record<DatePart, number> = { year: 0, month: 0, day: 0 };
  for (let i = 0; i < 3; i++) {
    if (!/^\d+$/.test(segments[i])) return null;
    parts[order[i]] = parseInt(segments[i], 10);
  }
  const { year, month, day } = parts;
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) return null;

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return String(date.getTime());
};

// ASSETS
